import { useState, useEffect, useRef } from 'react';
import { apiCall } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import styles from './JobsPanel.module.css';

interface SendJob {
  jobId: number;
  studyId: number;
  seriesId: number;
  destId: number;
  status: string;
  totalImages: number;
  sentImages: number;
  error: string;
  destination: string;
  // Study/patient details
  patId: string;
  patFirstName: string;
  patLastName: string;
  patSex: string;
  studyDate: string;
  studyDesc: string;
  patientName: string;
  patientId: string;
  studyDescription: string;
  modality: string;
  accessionNumber: string;
  createdAt: string;
}

interface Props {
  refreshTrigger: number;
  onJobCreated?: () => void;
}

const POLL_INTERVAL = 3000;
const SLOW_POLL_INTERVAL = 30000;

export default function JobsPanel({ refreshTrigger, onJobCreated }: Props) {
  const [jobs, setJobs] = useState<SendJob[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [retrying, setRetrying] = useState<number | null>(null);
  const userId = useAuthStore((s) => s.userId);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetchJobs();
  }, [refreshTrigger]);

  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === 'pending' || j.status === 'sending');
    const interval = hasActive ? POLL_INTERVAL : SLOW_POLL_INTERVAL;

    if (hasActive) setExpanded(true);

    pollRef.current = setInterval(fetchJobs, interval);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobs.length, jobs.some?.((j) => j.status === 'pending' || j.status === 'sending')]);

  async function fetchJobs() {
    if (!userId) return;
    try {
      const res = await apiCall<SendJob>('study.sendstatus', { userId });
      if (res.s === 'ok') {
        setJobs(res.data || []);
      }
    } catch { /* ignore */ }
  }

  async function retryJob(job: SendJob) {
    setRetrying(job.jobId);
    try {
      const res = await apiCall('study.send', {
        studyId: job.studyId,
        seriesId: job.seriesId,
        destId: job.destId,
      });
      if (res.s === 'ok') {
        onJobCreated?.();
        fetchJobs();
      }
    } catch { /* ignore */ }
    finally {
      setRetrying(null);
    }
  }

  const activeCount = jobs.filter(
    (j) => j.status === 'pending' || j.status === 'sending'
  ).length;

  return (
    <div className={styles.panel}>
      <div className={styles.header} onClick={() => setExpanded(!expanded)}>
        <span className={styles.headerLabel}>
          Send Jobs
          {activeCount > 0 && (
            <span className={styles.activeBadge}>{activeCount} active</span>
          )}
          {activeCount === 0 && jobs.length > 0 && (
            <span className={styles.countBadge}>{jobs.length}</span>
          )}
        </span>
        <span className={styles.toggle}>{expanded ? '▼' : '▲'}</span>
      </div>

      {expanded && (
        <div className={styles.list}>
          {jobs.length === 0 && (
            <div className={styles.emptyMsg}>No recent send jobs</div>
          )}
          {jobs.map((job) => {
            const pct = job.totalImages > 0
              ? Math.round((job.sentImages / job.totalImages) * 100)
              : 0;

            return (
              <div key={job.jobId} className={`${styles.job} ${styles['jobBorder_' + job.status]}`}>
                {/* Row 1: Patient + Status */}
                <div className={styles.jobTop}>
                  <div className={styles.jobStudyInfo}>
                    <span className={styles.jobPatient}>
                      {[job.patLastName, job.patFirstName].filter(Boolean).join('^') || job.patientName || 'Unknown'}
                    </span>
                    {job.patSex && <span className={styles.jobSex}>{job.patSex}</span>}
                    {job.modality && <span className={styles.jobModality}>{job.modality}</span>}
                  </div>
                  <span className={`${styles.jobStatus} ${styles['status_' + job.status]}`}>
                    {job.status === 'sending'
                      ? `${pct}%`
                      : job.status}
                  </span>
                </div>

                {/* Row 2: Study details */}
                <div className={styles.jobDetails}>
                  <span>{job.studyDesc || job.studyDescription}</span>
                  <span>ID: {job.patId || job.patientId}</span>
                  {job.studyDate && <span>{job.studyDate}</span>}
                  {job.accessionNumber && <span>Acc: {job.accessionNumber}</span>}
                </div>

                {/* Row 3: Destination + progress */}
                <div className={styles.jobDestRow}>
                  <span className={styles.jobDestLabel}>To: {job.destination}</span>
                  {job.status === 'sending' && (
                    <span className={styles.jobProgress}>
                      {job.sentImages}/{job.totalImages} images
                    </span>
                  )}
                  {job.status === 'completed' && (
                    <span className={styles.jobProgress}>
                      {job.totalImages} images sent
                    </span>
                  )}
                </div>

                {/* Progress bar */}
                {(job.status === 'sending' || job.status === 'pending') && (
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}

                {/* Error + Retry */}
                {job.status === 'error' && (
                  <div className={styles.jobErrorRow}>
                    <span className={styles.jobError}>{job.error}</span>
                    <button
                      className={styles.retryBtn}
                      onClick={() => retryJob(job)}
                      disabled={retrying === job.jobId}
                    >
                      {retrying === job.jobId ? 'Retrying...' : 'Retry'}
                    </button>
                  </div>
                )}

                {/* Timestamp */}
                <div className={styles.jobTime}>{job.createdAt}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
